import { CentralAdminEntityType } from "Enums/CentralAdminEntityType";
import { ScopeHierarchyId, ScopeItem } from "../Scopes.types";
import {
    centralAdminCapacityUnitSchemaFromTopologyService,
    centralAdminDagSchema,
    centralAdminFaultZoneSchema,
    centralAdminForestSchema,
    centralAdminItemSchema,
    centralAdminLoadBalancerSchema,
    centralAdminLocationSchema,
    centralAdminMachineSchema,
    centralAdminRackSchema
} from "./CentralAdminTopology";
import { targetTypeToString } from "../ScopeSearch.types";

export const convertResultToScopeItems = (result: unknown, targetType: CentralAdminEntityType, scopeHierarchyId: string): ScopeItem[] => {
    switch (targetType) {
        case CentralAdminEntityType.Region:
            return convertRegionResult(result);
        case CentralAdminEntityType.Forest:
            return convertForestResult(result, scopeHierarchyId);
        case CentralAdminEntityType.Dag:
            return convertDagResult(result);
        case CentralAdminEntityType.Machine:
            return convertMachineResult(result);
        case CentralAdminEntityType.Location:
            return convertLocationResult(result);
        case CentralAdminEntityType.Rack:
            return convertRackResult(result);
        case CentralAdminEntityType.CapacityUnit:
            return convertCapacityUnitResult(result);
        case CentralAdminEntityType.LoadBalancer:
            return convertLoadBalancerResult(result);
        case CentralAdminEntityType.FaultZone:
            return convertFaultZoneResult(result);
        default:
            return [];
    }
};

const convertRegionResult = (result: unknown): ScopeItem[] => {
    const regionResult = centralAdminItemSchema.parse(result);
    return [{ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: regionResult.Name ?? "" }];
};

const convertForestResult = (result: unknown, scopeHierarchyId: string): ScopeItem[] => {
    const forestResult = centralAdminForestSchema.parse(result);
    const isAccountForest = forestResult.Type?.toLowerCase().includes("account");
    const resultScopes: ScopeItem[] = [];
    if (scopeHierarchyId === ScopeHierarchyId.SubstrateAccountForestScopeHierarchy && !isAccountForest) {
        return resultScopes;
    }
    if (forestResult.DeploymentRing) {
        resultScopes.push(getRingScopeItem(forestResult.DeploymentRing));
    }
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: forestResult.Region ?? "" });
    resultScopes.push(getForestScopeItem(forestResult.Name ?? "", forestResult.Alias ?? "", !!isAccountForest));
    return resultScopes;
};

const convertDagResult = (result: unknown): ScopeItem[] => {
    const dagResult = centralAdminDagSchema.parse(result);
    const resultScopes = [];
    if (dagResult.DeploymentRing) {
        resultScopes.push(getRingScopeItem(dagResult.DeploymentRing));
    }
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: dagResult.Region ?? "" });
    resultScopes.push(getForestScopeItem(dagResult.Forest ?? "", dagResult.ForestAlias ?? "", false));
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Dag], Value: dagResult.Name ?? "" });
    return resultScopes;
};

const convertMachineResult = (result: unknown): ScopeItem[] => {
    const machineResult = centralAdminMachineSchema.parse(result);
    const resultScopes = [];
    if (machineResult.DeploymentRing) {
        resultScopes.push(getRingScopeItem(machineResult.DeploymentRing));
    }
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: machineResult.Region ?? "" });
    resultScopes.push(getForestScopeItem(machineResult.Forest ?? "", machineResult.ForestAlias ?? "", false));
    if (machineResult.ExchangeFaultZone) {
        resultScopes.pop();
    }
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Dag], Value: machineResult.Dag ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.FaultZone], Value: machineResult.ExchangeFaultZone ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Location], Value: machineResult.Location ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.CapacityUnit], Value: machineResult.CapacityUnit ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Machine], Value: machineResult.Name ?? "" });
    return resultScopes;
};

const convertLocationResult = (result: unknown): ScopeItem[] => {
    const locationResult = centralAdminLocationSchema.parse(result);
    const resultScopes = [];
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: locationResult.Region });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Location], Value: locationResult.Name ?? "" });
    return resultScopes;
};

const convertRackResult = (result: unknown): ScopeItem[] => {
    const rackResult = centralAdminRackSchema.parse(result);
    const resultScopes = [];
    if (rackResult.Environment) {
        resultScopes.push(getEnvironmentScopeItem(rackResult.Environment));
    }
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: rackResult.Region ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Location], Value: rackResult.Location ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Rack], Value: rackResult.Name ?? "" });
    return resultScopes;
};

const convertCapacityUnitResult = (result: unknown): ScopeItem[] => {
    const capacityUnitResult = centralAdminCapacityUnitSchemaFromTopologyService.parse(result);
    const resultScopes = [];
    if (capacityUnitResult.Environment) {
        resultScopes.push(getEnvironmentScopeItem(capacityUnitResult.Environment));
    }
    if (capacityUnitResult.DeploymentRing) {
        resultScopes.push(getRingScopeItem(capacityUnitResult.DeploymentRing));
    }
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: capacityUnitResult.Region ?? "" });
    resultScopes.push(getForestScopeItem(capacityUnitResult.Forest ?? "", capacityUnitResult.ForestAlias ?? "", false));
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.FaultZone], Value: capacityUnitResult.ExchangeFaultZone ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.CapacityUnit], Value: capacityUnitResult.Name ?? "" });
    return resultScopes;
};

const convertLoadBalancerResult = (result: unknown): ScopeItem[] => {
    const loadBalancerResult = centralAdminLoadBalancerSchema.parse(result);
    const resultScopes = [];
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: loadBalancerResult.Region ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Location], Value: loadBalancerResult.Location ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Rack], Value: loadBalancerResult.Rack ?? "" });
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.LoadBalancer], Value: loadBalancerResult.Name ?? "" });
    return resultScopes;
};

const convertFaultZoneResult = (result: unknown): ScopeItem[] => {
    const faultZoneResult = centralAdminFaultZoneSchema.parse(result);
    const resultScopes = [];
    if (faultZoneResult.Regions?.length === 1) {
        resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.Region], Value: faultZoneResult.Regions[0] });
    }
    resultScopes.push({ ScopeType: targetTypeToString[CentralAdminEntityType.FaultZone], Value: faultZoneResult.Name ?? "" });
    return resultScopes;
};

const getEnvironmentScopeItem = (environment: string): ScopeItem => ({
    ScopeType: "Environment",
    Value: environment
});

const getRingScopeItem = (ring: string): ScopeItem => {
    const rings = ring.split(",");
    const ringName = rings.find((r) => r.trim().toLowerCase() === "sdfv2") ?? rings[0] ?? "";
    return {
        ScopeType: "Ring",
        Value: ringName
    };
};

const getForestScopeItem = (forest: string, alias: string, isAccountForest: boolean): ScopeItem => {
    const forestName = forest.toUpperCase();
    const forestNameAlias = forestName.includes(alias) ? forestName : `${forestName} (${alias})`;
    return {
        ScopeType: isAccountForest ? "AccountForest" : targetTypeToString[CentralAdminEntityType.Forest],
        Value: forestName,
        ExtraProperties: {
            Alias: forestNameAlias,
            ShortAlias: alias
        }
    };
};
